// Email body cleaner — strips signatures, quoted replies, attachment markers,
// CID references, URL tail-bloat, and auto-responder bloat so that each
// MessageBubble only holds what the sender actually wrote this turn.
//
// Philosophy: prefer false negatives (leave stuff alone) over false positives
// (strip real content). That's why `cleanEmailBody` also returns a `removedLines`
// count — if the UI sees something was cut, it can surface a "Show original"
// toggle. The raw body is always still available on `message.body_raw`.
//
// Heuristics (in order):
//   0. Strip Zendesk-style notification preambles ("You have a new message
//      from Market Dwellings/Harper + Kellee", "##- Please type your reply
//      above this line -##", and friends). When the reply-above marker is
//      present we ALSO cut everything after it — the rest is quoted history.
//   1. Strip boilerplate "On <date>, <name> wrote:" quote headers and every
//      subsequent line (email clients always append quoted history AT THE END).
//   2. Strip GMail's "This is an automated reply" openers.
//   3. Strip attachment blocks that Outlook/Zendesk paste inline:
//        Attachment(s):
//            Screenshot 2026-03-13 at 10.44.32 AM.png
//   4. Strip CID image references: [cid:abc-123-...].
//   5. Strip URL tail-bloat like "www.example.com-http://www.example.com/>".
//   6. Strip obvious signature blocks: the LAST occurrence of "Sent from my
//      iPhone", "Get Outlook for...", a standalone "--" line, or a detected
//      sig fingerprint (3+ consecutive lines containing phone/email/address
//      patterns).
//   7. Collapse runs of 3+ blank lines to 2.
//   8. Trim leading/trailing whitespace.

// Zendesk's "type your reply above this line" divider — universal across
// brands. Variants: with #, with -, with em dash, with extra whitespace.
const REPLY_ABOVE_RE =
  /^[\s#\-=*]*\s*(?:please\s+)?(?:type|write|reply|respond)\s+(?:your\s+)?(?:reply|response|message)?\s*above\s+(?:this|the)\s+line[\s#\-=*.!?]*$/im;

// Zendesk-style notification line. We strip the line itself wherever it
// appears (start, middle, or after the divider).
const NOTIFICATION_PREAMBLE_RE =
  /^\s*(?:You\s+have\s+a\s+new\s+(?:message|reply|comment|note)\s+from\s+(?:Market\s+Dwellings|Harper\s*\+?\s*Kellee|HarperKellee|HK|MD|[\w&'\s.+-]{2,40})|This\s+(?:email|message)\s+(?:is\s+)?in\s+(?:reply|response)\s+to|The\s+following\s+(?:message|reply)\s+was\s+(?:sent|posted)\s+to|A\s+new\s+(?:message|reply)\s+has\s+been\s+(?:posted|received)\s+(?:on|to)|Re:\s+\[Ticket\s+#?\d+\]|---?\s*Forwarded\s+message\s*---?)\s*[:.-]*\s*$/im;

const QUOTE_HEADER_RE =
  /^\s*(On\s.+?\s(?:at\s)?\d{1,2}:\d{2}\s?(?:AM|PM|am|pm)?,?\s.*?\s(?:wrote|said):|From:\s.*|-{2,}\s*Original\s+Message\s*-{2,}|_{3,}\s*$)/m;

const ATTACHMENT_BLOCK_RE =
  /^\s*Attachment\(s\):\s*\n(?:[\s\S]*?(?:\.(?:png|jpg|jpeg|gif|pdf|heic|svg|webp|doc|docx|xls|xlsx|csv|ppt|pptx|zip))[\s\S]*?)+$/mi;

const CID_RE = /\[cid:[^\]]+\]/g;
const URL_BLOAT_RE = /<https?:\/\/[^>]+>/g;
const DOUBLE_URL_RE = /\b([\w.-]+\.(?:com|net|org|io|co))-https?:\/\/\1\/?>/gi;
const SENT_FROM_MOBILE_RE =
  /^\s*(Sent from my\s+\w+|Get Outlook for\s+\w+|Sent via (?:BlackBerry|the\s+\w+ app))[\s\S]*$/im;

// Lines that look like sig metadata: phone, email, street address, URL.
const PHONE_LINE_RE = /^(?:\s*(?:Phone|Mobile|Cell|Tel|Fax|Office|Work):?)?\s*[\d().+\-\s]{7,}$/;
const EMAIL_LINE_RE = /^\s*(?:Email:?)?\s*[\w.+-]+@[\w-]+\.[\w.-]+(?:\s*[-—]\s*mailto:.*)?$/i;
const ADDRESS_LINE_RE = /^\s*\d+\s+\w+.*(?:street|st|ave|avenue|rd|road|blvd|ste|suite|drive|dr|ln|lane|way|pkwy|hwy|court|ct|pl|place|circle|cir).*/i;
const URL_LINE_RE = /^\s*(?:www\.|https?:\/\/)\S+\s*$/i;
const ZIP_LINE_RE = /^\s*[\w\s,.]*?\b\d{5}(?:-\d{4})?\b.*$/;
const ROLE_LINE_RE = /^\s*(?:Marketing|Sales|Customer|Account|Senior|Junior|Lead|Head|Director|Manager|Assistant|Associate|Executive|Coordinator|Specialist|Owner|Founder|Realtor|Agent|Broker)[\s\w]*$/i;

function looksLikeSignatureLine(line: string): boolean {
  const t = line.trim();
  if (t.length === 0) return false;
  if (t.length > 120) return false; // signatures are short lines
  if (PHONE_LINE_RE.test(t)) return true;
  if (EMAIL_LINE_RE.test(t)) return true;
  if (ADDRESS_LINE_RE.test(t)) return true;
  if (URL_LINE_RE.test(t)) return true;
  if (ZIP_LINE_RE.test(t)) return true;
  if (ROLE_LINE_RE.test(t)) return true;
  return false;
}

/**
 * Find the signature starting line by scanning BACKWARD from the end. The
 * signature is the last contiguous block of sig-shaped lines (phone, email,
 * address, title, URL, zip) separated from body content by at most one blank
 * line. If we find fewer than 2 sig-shaped lines, we leave the body alone.
 *
 * Returns the 0-based line index where the signature starts, or -1 if no
 * signature was detected.
 */
function detectSignatureStart(lines: string[]): number {
  if (lines.length < 3) return -1;

  // Walk from end, skipping trailing empty lines
  let end = lines.length - 1;
  while (end >= 0 && lines[end]!.trim().length === 0) end--;
  if (end < 2) return -1;

  // Collect the tail block of sig-shaped + name-shaped lines (allow 1 blank gap)
  let sigStart = -1;
  let sigShaped = 0;
  let blanksSinceSig = 0;
  for (let i = end; i >= 0; i--) {
    const line = lines[i]!;
    if (line.trim().length === 0) {
      // Blank line — allow one, then stop
      if (blanksSinceSig >= 1) break;
      blanksSinceSig++;
      continue;
    }
    if (looksLikeSignatureLine(line)) {
      sigStart = i;
      sigShaped++;
      blanksSinceSig = 0;
      continue;
    }
    // A non-blank, non-sig line. This might be the person's name (first line
    // of sig, typical pattern: "Name\nRole\nCompany\nPhone..."). Accept up to
    // 2 "name-shaped" lines right before the sig cluster.
    if (sigShaped >= 2 && line.trim().length < 60 && /^[A-Z]/.test(line.trim())) {
      sigStart = i;
      continue;
    }
    break;
  }

  return sigShaped >= 2 ? sigStart : -1;
}

/**
 * Also detect the explicit "--" signature delimiter (a line containing only
 * "-- "). When present, the signature starts at that line. This is the
 * RFC 3676 convention and the most reliable sig marker.
 */
function detectDelimiterSignatureStart(lines: string[]): number {
  for (let i = lines.length - 1; i >= 0; i--) {
    const t = lines[i]!.trim();
    if (t === '--' || t === '--' || t === '—') return i;
  }
  return -1;
}

export interface CleanedBody {
  /** The cleaned body ready for display. */
  text: string;
  /** True if any cleanup trimmed content out of the original. */
  hasRemovedContent: boolean;
  /** Rough line count of how much we removed (for debug / toggle label). */
  removedLines: number;
}

/**
 * Clean an email body for display. Safe to run on any direction (inbound or
 * outbound) — if you're rendering a message Kimi sent, you probably want to
 * strip her own signature from the bubble too.
 */
export function cleanEmailBody(raw: string | null | undefined): CleanedBody {
  if (!raw) return { text: '', hasRemovedContent: false, removedLines: 0 };
  const original = raw;
  let text = raw;

  // 0a. Cut at "##- type your reply above this line -##" — everything below
  // is quoted history. Customer replies typically sit ABOVE this marker.
  const aboveMatch = REPLY_ABOVE_RE.exec(text);
  if (aboveMatch && aboveMatch.index > 0) {
    text = text.slice(0, aboveMatch.index);
  } else if (aboveMatch && aboveMatch.index === 0) {
    // Marker is the very first line — cut everything below it (rare but happens
    // when the customer typed nothing above the divider).
    const after = text.slice(aboveMatch.index + aboveMatch[0].length);
    text = ''.concat(after);
  }

  // 0b. Strip Zendesk-style "You have a new message from <brand>" preamble
  // lines anywhere they appear. We strip the line itself, not the rest of the
  // body — sometimes the customer's actual message follows immediately.
  text = text
    .split('\n')
    .filter((line) => !NOTIFICATION_PREAMBLE_RE.test(line))
    .join('\n');

  // 0c. Strip "EXTERNAL EMAIL" warning banners that brokerage / corporate
  // mail servers prepend. Common shapes:
  //   "[EXTERNAL] ..." (single bracket prefix)
  //   "*** EXTERNAL EMAIL *** ..." (delimited line)
  //   "CAUTION: This email originated from outside..." (multi-line block)
  text = text
    .split('\n')
    .filter(
      (line) =>
        !/^\s*(\[?\*{0,3}\s*EXTERNAL(?:\s+EMAIL)?\s*\*{0,3}\]?|CAUTION:\s+This\s+email\s+originated|External\s+email\.\s+Do\s+not\s+click)/i.test(
          line,
        ),
    )
    .join('\n');

  // 0d. Shopify contact-form bodies. The default notification ships the
  // form fields inline like:
  //
  //   Country Code:US
  //   Name:Rachel Hovey
  //   Email:rachel.hovey@bhhsamb.com
  //   Phone Number:4029374660
  //   Comment:Hi, I just set up a profile with you. Our Brokerage is...
  //
  // Kimi doesn't need to see the Country Code / Name / Email / Phone Number
  // headers — those fields already render in the customer panel, order
  // panel, and topbar. Collapse the body to just the Comment: text so the
  // bubble is short and the "Show original" toggle appears for the raw form.
  //
  // Detection: at least 3 of the 5 canonical field headers present. Extraction:
  // everything from the Comment: marker to the end (or signature trigger).
  // If Comment: is missing, we still strip the other headers but leave whatever
  // free-form text follows so we don't accidentally zero-out the body.
  const contactFieldRe = /^\s*(?:Country\s*Code|Name|Email|Phone\s*Number|Phone|Company|Address)\s*:\s*/im;
  const fieldLineCount = text
    .split('\n')
    .filter((line) => contactFieldRe.test(line)).length;
  if (fieldLineCount >= 3) {
    const commentMatch = /^\s*Comment\s*:\s*/im.exec(text);
    if (commentMatch) {
      // Take everything from the Comment: marker onward, stripping the
      // "Comment:" prefix from the first line.
      const after = text.slice(commentMatch.index + commentMatch[0].length);
      text = after;
    } else {
      // No Comment: marker — strip the canonical field lines so the body
      // at least doesn't lead with metadata.
      text = text
        .split('\n')
        .filter((line) => !contactFieldRe.test(line))
        .join('\n');
    }
  }

  // 1. Cut at the first quoted-reply header.
  const quoteMatch = QUOTE_HEADER_RE.exec(text);
  if (quoteMatch && quoteMatch.index > 0) {
    text = text.slice(0, quoteMatch.index);
  }

  // 2. Strip "> " quoted lines anywhere that remain.
  text = text
    .split('\n')
    .filter((line) => !/^\s*>\s?/.test(line))
    .join('\n');

  // 3. Strip inline attachment blocks.
  text = text.replace(ATTACHMENT_BLOCK_RE, '');

  // 4. Strip CID refs.
  text = text.replace(CID_RE, '');

  // 5. Strip URL bloat like <https://...> and www.x.com-http://x.com/> artifacts.
  text = text.replace(DOUBLE_URL_RE, '$1');
  text = text.replace(URL_BLOAT_RE, '');

  // 6a. Strip "Sent from my iPhone" style footers and anything after them.
  const sentFromIdx = text.search(SENT_FROM_MOBILE_RE);
  if (sentFromIdx !== -1) text = text.slice(0, sentFromIdx);

  // 6b. Signature detection.
  const lines = text.split('\n');
  const delimIdx = detectDelimiterSignatureStart(lines);
  if (delimIdx !== -1) {
    text = lines.slice(0, delimIdx).join('\n');
  } else {
    const sigIdx = detectSignatureStart(lines);
    if (sigIdx !== -1) {
      text = lines.slice(0, sigIdx).join('\n');
    }
  }

  // 7. Collapse runs of 3+ blank lines to 2, then trim.
  text = text.replace(/\n{3,}/g, '\n\n').trim();

  const originalLineCount = original.split('\n').length;
  const cleanedLineCount = text.split('\n').length;
  const removedLines = Math.max(0, originalLineCount - cleanedLineCount);

  return {
    text,
    hasRemovedContent: text.length !== original.trim().length,
    removedLines,
  };
}

/** Subject cleaner — strips "Re: ", "Fwd:", and trailing "(16392)" /
 *  "[#16392]" / "[Ticket #16392]" style ticket ids that Zendesk likes to inject.
 *
 *  Also rewrites Shopify contact-form default subjects like "You have a new
 *  message from Market Dwellings" to something the UI can show without
 *  looking like leaked boilerplate. We can't always tell what the topic
 *  actually is, so we fall back to "Contact form submission".
 */
export function cleanSubject(raw: string | null | undefined): string {
  if (!raw) return '';
  const cleaned = raw
    .replace(/^\s*(?:Re|Fwd?|FW|AW):\s*/i, '')
    .replace(/^\s*(?:Re|Fwd?|FW|AW):\s*/i, '') // run twice for "Re: Fwd: ..."
    .replace(/\s*\(\d+\)\s*$/, '')
    .replace(/\s*\[(?:Ticket\s*)?#?\d+\]\s*$/i, '')
    .trim();

  // Shopify contact-form default. Matches both brands.
  if (
    /^\s*You\s+have\s+a\s+new\s+(?:message|enquiry|inquiry)\s+from\s+(?:Market\s+Dwellings|Harper\s*\+?\s*Kellee|HarperKellee)\s*$/i.test(
      cleaned,
    )
  ) {
    return 'Contact form submission';
  }

  return cleaned;
}

/**
 * prettifyEmailLocal — last-ditch human-name guess from an email address.
 *
 * We use this when neither Shopify nor the From-header gave us a real name
 * but we still need SOMETHING in the big Larken header on CustomerPanel.
 * Showing "sarah.fellah@provaus.com" in 38px serif looks impersonal and
 * just duplicates the email line right beneath it.
 *
 * Examples:
 *   "sarah.fellah@provaus.com"           -> "Sarah Fellah"
 *   "sarah_fellah_realtor@gmail.com"     -> "Sarah Fellah Realtor"
 *   "rachel-hovey99@bhhsamb.com"         -> "Rachel Hovey"
 *   "john+inbox@example.com"             -> "John"
 *   "info@bigcorp.com"                   -> "Info"  (we don't know better)
 *   "noreply@anywhere.com"               -> null    (clearly not a person)
 *   ""                                   -> null
 *
 * Rules:
 * - Split on . _ - + and remove the +tag suffix.
 * - Drop trailing digits ("hovey99" -> "hovey").
 * - Title-case each remaining token.
 * - Drop tokens that are pure digits or empty.
 * - Return null for known non-human prefixes (noreply, no-reply, donotreply,
 *   mailer-daemon, postmaster, support, hello, hi, contact, admin, sales,
 *   billing, orders, info — when used alone) so the caller can fall back
 *   to the email itself for those (b2b inboxes, etc).
 *   Note: if the local part has more than one word, we keep it — "support
 *   team" or "sales rep" don't typically appear as multi-word locals.
 */
const NON_HUMAN_LOCALS = new Set([
  'noreply',
  'no-reply',
  'donotreply',
  'do-not-reply',
  'mailer-daemon',
  'postmaster',
  'bounce',
  'bounces',
]);

export function prettifyEmailLocal(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.indexOf('@');
  if (at <= 0) return null;
  const localRaw = email.slice(0, at).toLowerCase();
  // Strip "+tag" suffix used for inbox routing.
  const local = localRaw.split('+')[0] ?? localRaw;
  if (!local) return null;
  if (NON_HUMAN_LOCALS.has(local)) return null;
  const tokens = local
    .split(/[._\-]+/)
    .map((tok) => tok.replace(/\d+$/g, '')) // drop trailing digits
    .filter((tok) => tok.length > 0 && !/^\d+$/.test(tok));
  if (tokens.length === 0) return null;
  return tokens
    .map((tok) => tok.charAt(0).toUpperCase() + tok.slice(1))
    .join(' ');
}
